import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  ListSubheader,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SpeedIcon from '@mui/icons-material/Speed';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ForumIcon from '@mui/icons-material/Forum';
import FolderIcon from '@mui/icons-material/Folder';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import authService from '../services/authService';
import UnionSwitcher from './UnionSwitcher';
import ApartmentSwitcher from './ApartmentSwitcher';
import { useUnion } from '../contexts/UnionContext';
import { useApartment } from '../contexts/ApartmentContext';

const drawerWidth = 240;

function Layout({ children }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isUnionAdmin } = useUnion();
  const { activeApartment } = useApartment();
  const isAdminView = isUnionAdmin && !activeApartment;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  // Items scoped to the active apartment
  const aptItems = [
    { text: t('nav.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('nav.meterReadings'), icon: <SpeedIcon />, path: '/meter-readings' },
    { text: t('nav.sentInvoices'), icon: <ReceiptLongIcon />, path: '/sent-invoices' },
    { text: t('nav.forum'), icon: <ForumIcon />, path: '/forum' },
  ];

  // Items scoped to the active union (visible in admin view)
  const unionItems = isAdminView ? [
    { text: t('nav.apartments'), icon: <ApartmentIcon />, path: '/apartments' },
    { text: t('nav.transactions'), icon: <AccountBalanceIcon />, path: '/transactions' },
    { text: t('nav.unions'), icon: <BusinessIcon />, path: '/admin/unions' },
  ] : [];

  // Admin-only union management items
  const adminItems = isAdminView ? [
    { text: t('nav.billingPeriods'), icon: <ReceiptIcon />, path: '/billing-periods' },
    { text: t('nav.members'), icon: <PeopleIcon />, path: '/admin/members' },
    { text: t('nav.utilityTypes'), icon: <SettingsIcon />, path: '/admin/utility-types' },
    { text: t('nav.files'), icon: <FolderIcon />, path: '/admin/files' },
  ] : [];

  const renderItems = (items) => items.map((item) => (
    <ListItem key={item.text} disablePadding>
      <ListItemButton onClick={() => navigate(item.path)}>
        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    </ListItem>
  ));

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          NÄIT.EE
        </Typography>
      </Toolbar>
      <Divider />
      <List
        subheader={
          <ListSubheader disableSticky sx={{ lineHeight: '32px', mt: 0.5 }}>
            {t('nav.apartment')}
          </ListSubheader>
        }
      >
        {renderItems(aptItems)}
      </List>
      {isAdminView && (
        <>
          <Divider />
          <List
            subheader={
              <ListSubheader disableSticky sx={{ lineHeight: '32px', mt: 0.5 }}>
                {t('nav.union')}
              </ListSubheader>
            }
          >
            {renderItems(unionItems)}
            {renderItems(adminItems)}
          </List>
        </>
      )}
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <UnionSwitcher />
          <ApartmentSwitcher />
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ flexGrow: 0 }}>
            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
              <Avatar alt="User" />
            </IconButton>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/profile'); }}>
                <AccountCircleIcon sx={{ mr: 1 }} />
                <Typography textAlign="center">{t('nav.profile')}</Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                <Typography textAlign="center">{t('nav.logout')}</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Container maxWidth="lg">
          {children}
        </Container>
      </Box>
    </Box>
  );
}

export default Layout;
